'use client'

import {
  IconCircleCheck,
  IconCircleX,
  IconCreditCard,
  IconSearch
} from '@tabler/icons-react'
import clsx from 'clsx'
import Head from 'next/head'

import { formatDeliveryExtendedData } from 'modules/delivery'
import { IS_TMS_DELIVERY } from 'shared/config'
import Button from 'shared/ui/Button'
import Link, { LinkProps } from 'shared/ui/Link/Link'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { theme } from 'shared/ui/utils/designTokens'
import { PATHS } from 'shared/utils/paths'

import RepeatSaleOrderButton from '../../../components/RepeatSaleOrderButton'
import { SaleOrder } from '../../../types'
import { showPaymentDifference } from '../../editSaleOrder/utils'

import styles from './SaleOrderSummary.module.scss'

interface SaleOrderSummaryProps {
  order: SaleOrder
  handlePaymentStart: () => void
  paymentError: boolean
}

export const SaleOrderSummary = ({
  order: {
    code,
    deliverySlot,
    deliveryAddressStreet: street,
    deliveryAddressCity: city,
    deliveryAddressPostalCode: postalCode,
    deliveryAddressHouseNumber: houseNumber,
    deliveryAddressFlatNumber: flatNumber,
    onlinePaymentRequired,
    status,
    items,
    paymentValue,
    editPaymentDifference,
    edited,
    id,
    tmsReservationSlotData
  },
  handlePaymentStart,
  paymentError
}: SaleOrderSummaryProps) => {
  const slotStart =
    IS_TMS_DELIVERY && tmsReservationSlotData
      ? tmsReservationSlotData?.slotExtended.from
      : deliverySlot?.start

  const slotEnd =
    IS_TMS_DELIVERY && tmsReservationSlotData
      ? tmsReservationSlotData?.slotExtended.to
      : deliverySlot?.end
  const date = deliverySlot?.date
  const price = paymentValue?.formatted

  const waitingForPayment =
    onlinePaymentRequired && status !== 'paid' && status !== 'accepted'

  return (
    <>
      <Head>
        <title>Podsumowanie zamówienia | Lokalny Rolnik</title>
      </Head>
      <div className={styles.wrapper}>
        {waitingForPayment && !paymentError ? (
          <div className={styles.payment}>
            <LoadingIndicator size={40} />
            Przetwarzanie płatności....
          </div>
        ) : paymentError ? (
          <>
            <IconCircleX
              width={80}
              height={80}
              strokeWidth={0.5}
              color={theme.colors.system.error.text}
            />
            <div className={styles.title}>Wystąpił błąd płatności</div>
            <div className={styles.subtitle}>
              Sprawdź swój adres email, aby zobaczyć co poszło nie tak
            </div>
            <div className={styles.errorDecisionLabel}>Co chcesz zrobić?</div>
            <div className={styles.buttonsWrapper}>
              <Button
                variant='neutralOutline'
                startIcon={<IconCreditCard />}
                onClick={handlePaymentStart}
              >
                Ponów płatność
              </Button>
            </div>
          </>
        ) : (
          <>
            <IconCircleCheck
              width={80}
              height={80}
              strokeWidth={0.5}
              color={theme.colors.brand.secondary}
            />
            <div className={styles.title}>
              {edited ? 'Edytowano zamówienie' : 'Przyjęliśmy Twoje zamówienie'}
            </div>
            <div className={styles.subtitle}>
              Twoje zamówienie zostało potwierdzone i zostanie dostarczone w
              wybranym przez Ciebie terminie.
            </div>
          </>
        )}
        <div className={styles.orderNumber}>
          Nr zamówienia: <span>{code || '———'}</span>
        </div>
        <div className={styles.orderDetailsWrapper}>
          <div className={styles.orderDetailsItem}>
            <div className={styles.orderDetailsItemLabel}>Termin dostawy</div>
            <div className={styles.orderDetailsItemValue}>{date}</div>
          </div>
          <div className={styles.orderDetailsItem}>
            <div className={styles.orderDetailsItemLabel}>Godziny dostawy</div>
            {IS_TMS_DELIVERY && tmsReservationSlotData ? (
              <div className={styles.orderDetailsItemValue}>
                {formatDeliveryExtendedData(
                  tmsReservationSlotData?.slotExtended.from,
                  tmsReservationSlotData?.slotExtended.to
                )}
              </div>
            ) : (
              <div className={styles.orderDetailsItemValue}>
                {slotStart}-{slotEnd}
              </div>
            )}
          </div>
          <div className={styles.orderDetailsItem}>
            <div className={styles.orderDetailsItemLabel}>Adres</div>
            <div className={styles.orderDetailsItemValue}>
              {`${street} ${houseNumber}${flatNumber ? `/${flatNumber}` : ''}`}
              <br />
              {`${postalCode} ${city}`}
            </div>
          </div>
          <div className={styles.orderDetailsItem}>
            <div className={styles.orderDetailsItemLabel}>Kwota</div>
            <div className={styles.orderDetailsItemValue}
              dangerouslySetInnerHTML={{ __html: price }}
            />
          </div>
          {showPaymentDifference({ editPaymentDifference, edited }) && (
            <div className={styles.orderDetailsItem}>
              <div className={styles.orderDetailsItemLabel}>
                {editPaymentDifference.type === 'refund' ? 'Zwrot' : 'Dopłata'}
              </div>
              <div className={styles.orderDetailsItemValue}
                dangerouslySetInnerHTML={{
                  __html: editPaymentDifference.value.formatted
                }}
              />
            </div>
          )}
          {showPaymentDifference({ editPaymentDifference, edited }) && (
            <div className={styles.orderDetailsItem}>
              <div className={styles.orderDetailsItemLabel}>
                {editPaymentDifference.type === 'refund' ? 'Zwrot' : 'Dopłata'}
              </div>
              <div className={styles.orderDetailsItemValue}
                dangerouslySetInnerHTML={{
                  __html: editPaymentDifference.value.formatted
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.buttonsWrapper}>
          <Link href={`${PATHS.profileOrders}?order=${id}`}>
            <Button variant='neutralOutline' startIcon={<IconSearch />}>
              Szczegóły zamówienia
            </Button>
          </Link>
          <RepeatSaleOrderButton
            className={styles.repeatOrder}
            items={items || []}
          />
        </div>
        <BackHome href={PATHS.home}>
          <Button variant='primary' size='large' fullWidth>
            Wróć do sklepu
          </Button>
        </BackHome>
      </div>
    </>
  )
}

export const BackHome = ({ className, ...props }: LinkProps) => (
  <Link {...props} className={clsx(styles.backHome, className)} />
)
